import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router';
import { SyncLoader } from 'halogenium';
import { colors } from 'helpers/ColorHelper';

import ExpiredToken from './ExpiredToken';
import AcknowledgeTicket from './AcknowledgeTicket/AcknowledgeTicket';
import { PAGE_STATES, SwitchPageStates } from './SwitchPageStates';

import {
  fetchMaintenanceTicket,
  updateVendorMaintenanceTicket,
  acknowledgeTicket,
} from '../store/actions';

const MaintenanceVendor = ({ location }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [pageState, setPageState] = useState(PAGE_STATES.form);
  const [ticketIsLoaded, setTicketIsLoaded] = useState(false);
  const [tokenIsExpired, setTokenIsExpired] = useState(false);
  const [temporaryValues, setTemporaryValues] = useState({});
  const [maintenanceTicket, setMaintenanceTicket] = useState({});

  const accessToken = location.query.access_token;

  const fetchData = useCallback(
    (accessToken) => {
      fetchMaintenanceTicket(accessToken)
        .done((response) => {
          setMaintenanceTicket(response?.maintenance_ticket);
          setAcknowledged(!!response?.resource_access_token?.acknowledged);
          setTicketIsLoaded(true);
        })
        .catch(() => {
          setTokenIsExpired(true);
        });
    },
    [fetchMaintenanceTicket]
  );

  const updateTicket = useCallback(
    async (data) => {
      setPageState(PAGE_STATES.loading);
      try {
        // if the request fails, store the data temporarily to retry
        setTemporaryValues(data);
        await updateVendorMaintenanceTicket(accessToken, data);
        setTemporaryValues({});
        setPageState(PAGE_STATES.updateTicketSuccess);
      } catch (err) {
        setPageState(PAGE_STATES.updateTicketError);
      }
    },
    [updateVendorMaintenanceTicket, accessToken]
  );

  const handleAcknowledge = useCallback(async () => {
    setPageState(PAGE_STATES.loading);
    try {
      await acknowledgeTicket(accessToken);
      setAcknowledged(true);
      setPageState(PAGE_STATES.acknowledgeTicketSuccess);
    } catch (err) {
      setPageState(PAGE_STATES.acknowledgeTicketError);
    }
  }, [acknowledgeTicket, accessToken]);

  useEffect(() => {
    fetchData(accessToken);
  }, [fetchData, accessToken]);

  const handleRetry = () => {
    updateTicket(temporaryValues);
  };

  const openForm = () => {
    setPageState(PAGE_STATES.form);
  };

  const openFormAfterUpdate = async () => {
    // re-fetch data in case the ticket is no longer editable
    await fetchData(accessToken);
    setPageState(PAGE_STATES.form);
  };

  if (tokenIsExpired) return <ExpiredToken />;
  if (!ticketIsLoaded) return <SyncLoader color={colors.YUHU_RED} size="12px" />;
  if (!acknowledged) return <AcknowledgeTicket handleAcknowledge={handleAcknowledge} />;
  if (ticketIsLoaded && acknowledged) {
    return (
      <SwitchPageStates
        pageState={pageState}
        handleRetry={handleRetry}
        handleAcknowledge={handleAcknowledge}
        openForm={openForm}
        openFormAfterUpdate={openFormAfterUpdate}
        maintenanceTicket={maintenanceTicket}
        updateTicket={updateTicket}
      />
    );
  }
};

export default withRouter(MaintenanceVendor);
